/**
 * 来場者画面のエラー境界
 * 画面chunkの取得失敗や描画例外でアプリ全体が空白になるのを防ぎ、回復操作を表示します。
 */

import React from "react";
import AppButton from "./AppButton";
import {
  claimChunkReloadAttempt,
  isChunkLoadError,
} from "../utils/chunkLoadRecovery";
import { assignPage, reloadCurrentPage } from "../utils/browserNavigation";
import { logError } from "../utils/logger";

/** エラー境界のプロパティ */
type VisitorRouteErrorBoundaryProps = {
  /** 保護する来場者画面 */
  children: React.ReactNode;
  /** 「ホームへ戻る」の遷移先パス */
  homePath: string;
  /** 変化したらエラー表示を解除する値（現在のパス） */
  resetKey: string;
};

/** エラー境界の状態 */
type VisitorRouteErrorBoundaryState = {
  /** 捕捉した例外 */
  error: unknown;
  /** 自動再読み込みを見送った場合true */
  isAutoReloadBlocked: boolean;
};

/** 回復表示のプロパティ */
type VisitorRouteErrorFallbackProps = {
  /** 「ホームへ戻る」の遷移先パス */
  homePath: string;
  /** 自動再読み込み中ならtrue */
  isReloading: boolean;
};

/**
 * 画面を表示できない間の全画面回復表示
 * @param {VisitorRouteErrorFallbackProps} props - 表示状態
 * @returns {JSX.Element} 回復表示
 */
const VisitorRouteErrorFallback = ({
  homePath,
  isReloading,
}: VisitorRouteErrorFallbackProps) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app-quest-sky px-5 py-8">
      {isReloading ? (
        <div
          aria-busy="true"
          className="w-full max-w-sm border-4 border-app-quest-night bg-app-panel px-6 py-7 text-center shadow-[6px_6px_0_rgba(22,36,58,0.34)]"
          role="status"
        >
          <p className="text-xl font-black tracking-wide text-app-quest-night">
            最新の画面を読み込み直しています
          </p>
          <p className="mt-2 text-sm font-bold leading-6 text-app-text-secondary">
            アプリが更新されたため、自動で再読み込みします。少しだけお待ちください。
          </p>
        </div>
      ) : (
        <div
          className="w-full max-w-sm border-4 border-app-quest-night bg-app-panel px-6 py-7 shadow-[6px_6px_0_rgba(22,36,58,0.34)]"
          role="alert"
        >
          <p className="text-xl font-black tracking-wide text-app-quest-night">
            画面を読み込めませんでした
          </p>
          <p className="mt-2 text-sm font-bold leading-6 text-app-text-secondary">
            通信状況を確認して、再読み込みしてください。解決しない場合はB館前テントのスタッフまでお声がけください。
          </p>
          <div className="mt-5 flex flex-col gap-3">
            <AppButton label="再読み込みする" onPress={reloadCurrentPage} />
            <AppButton
              label="ホームへ戻る"
              onPress={() => assignPage(homePath)}
              variant="ghost"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 来場者画面の例外を捕捉し、chunk取得失敗なら1回だけ自動再読み込みする
 */
class VisitorRouteErrorBoundary extends React.Component<
  VisitorRouteErrorBoundaryProps,
  VisitorRouteErrorBoundaryState
> {
  state: VisitorRouteErrorBoundaryState = {
    error: null,
    isAutoReloadBlocked: false,
  };

  /**
   * 描画中の例外をエラー表示状態へ変換する
   * @param {unknown} error - 捕捉した例外
   * @returns {Partial<VisitorRouteErrorBoundaryState>} 次の状態
   */
  static getDerivedStateFromError(error: unknown): Partial<VisitorRouteErrorBoundaryState> {
    return { error, isAutoReloadBlocked: false };
  }

  /**
   * 例外を記録し、chunk取得失敗なら最新版へ再読み込みする
   * @param {unknown} error - 捕捉した例外
   * @returns {void}
   */
  componentDidCatch(error: unknown): void {
    /** chunk取得失敗か */
    const isChunkError = isChunkLoadError(error);

    logError("来場者画面の表示に失敗しました。", {
      errorMessage: error instanceof Error ? error.message : String(error),
      isChunkLoadError: isChunkError,
      path: typeof window === "undefined" ? "" : window.location.pathname,
    });

    if (isChunkError && claimChunkReloadAttempt()) {
      reloadCurrentPage();
      return;
    }

    this.setState({ isAutoReloadBlocked: true });
  }

  /**
   * 画面遷移したらエラー表示を解除する
   * @param {VisitorRouteErrorBoundaryProps} prevProps - 直前のプロパティ
   * @returns {void}
   */
  componentDidUpdate(prevProps: VisitorRouteErrorBoundaryProps): void {
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, isAutoReloadBlocked: false });
    }
  }

  /**
   * 画面または回復表示を描画する
   * @returns {React.ReactNode} 描画内容
   */
  render(): React.ReactNode {
    const { error, isAutoReloadBlocked } = this.state;

    if (error === null) {
      return this.props.children;
    }

    return (
      <VisitorRouteErrorFallback
        homePath={this.props.homePath}
        isReloading={isChunkLoadError(error) && !isAutoReloadBlocked}
      />
    );
  }
}

export default VisitorRouteErrorBoundary;
